import { useEffect, useState } from "react";
import { Wordmark } from "@onecpa/ui";
import { DEMO_ACCOUNTS, DEMO_PASSWORD } from "@onecpa/shared";
import { useAuth } from "../auth/AuthProvider.js";

declare const __API_BASE_URL__: string;

interface DevMembership {
  orgName: string;
  orgSlug: string;
  orgStatus: string;
  role: string;
  clientName: string | null;
}

interface DevAccount {
  email: string;
  fullName: string;
  memberships: DevMembership[];
}

const ROLE_LABELS: Record<string, string> = {
  owner: "בעל/ת המשרד",
  manager: "מנהל/ת משרד",
  staff: "מנהל/ת חשבונות",
  client_user: "לקוח",
};

const ORG_STATUS_NOTES: Record<string, string> = {
  trialing: "תקופת ניסיון",
  past_due: "ניסיון שפג",
  suspended: "מושהה",
};

/**
 * Sign-in, on paper rather than in a floating card — the Ledger system separates
 * with hairlines, and shadow-float is reserved for the marketing hero.
 *
 * Everything that reveals seeded credentials — the prefilled fields and the hint
 * panel — is gated on __SHOW_DEV_LOGINS__, a constant computed in vite.config.ts
 * from Vite's `command`.
 *
 * That indirection is deliberate. `import.meta.env.DEV` is spoofable from
 * configuration: NODE_ENV in the shared root .env made Vite treat `vite build` as
 * development, and a production bundle shipped this panel, password and all. A
 * VITE_ flag was no better, since the production build reads the same .env.
 * `command` is the only signal no env file can invert.
 */
declare const __SHOW_DEV_LOGINS__: boolean;

/**
 * The deployed demo, which needs the same picker for a different reason.
 *
 * It cannot reuse __SHOW_DEV_LOGINS__ — that is false in every build, which is
 * precisely the guarantee protecting the real app — and it cannot call
 * /__dev/accounts, which is not mounted outside development. It reads the
 * published roster instead, and vite.config.ts refuses to set this flag unless
 * the build also declares a demo host.
 */
declare const __DEMO_MODE__: boolean;

const SHOW_ACCOUNT_PICKER = __SHOW_DEV_LOGINS__ || __DEMO_MODE__;

export function SignIn() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState(SHOW_ACCOUNT_PICKER ? "owner@dkcpa.test" : "");
  const [password, setPassword] = useState(SHOW_ACCOUNT_PICKER ? DEMO_PASSWORD : "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * The seeded accounts, read from the running API rather than listed here.
   *
   * A hand-written list goes stale the moment the fixture grows, and this one had:
   * it offered four of seven accounts, omitting the manager and the second
   * bookkeeper — the two roles most worth clicking between.
   */
  const [accounts, setAccounts] = useState<DevAccount[]>(__DEMO_MODE__ ? DEMO_ACCOUNTS : []);
  useEffect(() => {
    if (!__SHOW_DEV_LOGINS__) return;
    let cancelled = false;

    fetch(`${__API_BASE_URL__}/__dev/accounts`)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload: { accounts?: DevAccount[] } | null) => {
        if (!cancelled && payload?.accounts) setAccounts(payload.accounts);
      })
      .catch(() => {
        // The panel is a convenience; the form still works without it.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function attempt(withEmail: string, withPassword: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await signIn(withEmail, withPassword);
    } catch {
      // Deliberately not distinguishing "no such user" from "wrong password":
      // that difference is an account-enumeration oracle.
      setError("הדוא״ל או הסיסמה אינם נכונים.");
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    await attempt(email, password);
  }

  /**
   * Clicking a seeded account signs in as them.
   *
   * The previous version only filled the email field and left the form sitting
   * there, which reads as nothing having happened — the field is above the list
   * and the change is one word. If the panel's whole purpose is switching persona
   * quickly, the click should switch persona.
   */
  async function signInAs(account: DevAccount): Promise<void> {
    setEmail(account.email);
    setPassword(DEMO_PASSWORD);
    await attempt(account.email, DEMO_PASSWORD);
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-paper px-5">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <Wordmark tone="dark" />
        </div>

        <h1 className="display-3 text-ink">כניסה למערכת</h1>
        <p className="mt-2 text-sm text-ink-muted">מערכת ההפעלה של משרד רואי החשבון.</p>

        {__DEMO_MODE__ && (
          <p className="mt-4 rounded-card border border-rule bg-wash/60 px-3 py-2.5 text-xs leading-relaxed text-ink-muted">
            <span className="font-semibold text-ink">סביבת הדגמה.</span> כל הנתונים
            מומצאים, המערכת פתוחה לכולם, וההודעות אינן נשלחות לאף אחד. הנתונים
            מתאפסים מדי לילה.
          </p>
        )}

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-ink">
              דוא״ל
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              // Email is Latin-first, so LTR is correct here rather than merely
              // isolated.
              dir="ltr"
              className="mt-1.5 w-full rounded-card border border-rule bg-surface px-3 py-2 text-base text-ink outline-none focus:border-ledger"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-ink">
              סיסמה
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              dir="ltr"
              className="mt-1.5 w-full rounded-card border border-rule bg-surface px-3 py-2 text-base text-ink outline-none focus:border-ledger"
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-card border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger-ink"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-card bg-ledger px-5 py-2.5 text-base font-semibold text-on-primary transition-colors hover:bg-primary-strong disabled:opacity-60"
          >
            {busy ? "מתחבר…" : "כניסה"}
          </button>
        </form>

        {SHOW_ACCOUNT_PICKER && accounts.length > 0 && (
          <div className="mt-8 border-t border-rule pt-4">
            <p className="text-2xs font-semibold text-ink-faint">
              {__DEMO_MODE__
                ? "בחרו תפקיד להתחברות — לחיצה נכנסת מיד"
                : "חשבונות לפיתוח מקומי — לחיצה נכנסת מיד"}
            </p>

            <ul className="mt-2.5 divide-y divide-line-soft border-y border-line-soft">
              {accounts.map((account) => (
                <li key={account.email}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void signInAs(account)}
                    className="flex w-full flex-col gap-0.5 py-2 text-start transition-colors hover:bg-wash/60 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ledger disabled:opacity-50"
                  >
                    <span className="flex items-baseline gap-2">
                      <span className="text-xs font-semibold text-ink">
                        {account.fullName}
                      </span>
                      <bdi className="text-2xs text-ink-faint" dir="ltr">
                        {account.email}
                      </bdi>
                    </span>

                    {account.memberships.length === 0 ? (
                      <span className="text-2xs text-ink-faint">ללא שיוך למשרד</span>
                    ) : (
                      account.memberships.map((membership) => (
                        <span
                          key={`${membership.orgSlug}-${membership.role}`}
                          className="text-2xs text-ink-muted"
                        >
                          {ROLE_LABELS[membership.role] ?? membership.role} · {membership.orgName}
                          {membership.clientName ? ` · ${membership.clientName}` : ""}
                          {ORG_STATUS_NOTES[membership.orgStatus]
                            ? ` · ${ORG_STATUS_NOTES[membership.orgStatus]}`
                            : ""}
                        </span>
                      ))
                    )}
                  </button>
                </li>
              ))}
            </ul>

            <p className="mt-2 text-2xs text-ink-faint">
              סיסמה לכולם: <span dir="ltr">{DEMO_PASSWORD}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
